/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, useIntl} from 'react-intl'
import {Button, Flex, Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {AbsoluteCenter, Spinner} from '@chakra-ui/react'
import {setSessionJSONItem} from '@salesforce/retail-react-app/app/utils/utils'
import {getSocialLoginManager} from '../../utils/social-utils'
import {sendViewErrorEvent} from '../../utils/gtm-utils'

// Icons
import {AppleIcon, GoogleIcon, SocialFacebookIcon} from '../icons'

import {
    API_ERROR_MESSAGE,
    FEATURE_UNAVAILABLE_ERROR_MESSAGE
} from '@salesforce/retail-react-app/app/constants'

const IDP_CONFIG = {
    apple: {
        icon: AppleIcon,
        message: defineMessage({
            id: 'login_form.button.apple',
            defaultMessage: 'Apple'
        })
    },
    google: {
        icon: GoogleIcon,
        message: defineMessage({
            id: 'login_form.button.google',
            defaultMessage: 'Google'
        })
    },
    facebook: {
        icon: SocialFacebookIcon,
        message: defineMessage({
            id: 'login_form.button.facebook',
            defaultMessage: 'Facebook'
        })
    }
}

/**
 * Create a stack of button for social login links
 * @param {array} idps - array of known IDPs to show buttons for
 * @returns
 */
const SocialLogin = ({form, idps = [], enabledIDPs}) => {
    const {formatMessage} = useIntl()
    const [isProcessingLogin, setIsProcessingLogin] = useState(false)

    const isIdpValid = (name) => {
        const idp = name.toLowerCase()
        const isConfigured = idp in IDP_CONFIG && IDP_CONFIG[idp]

        // Only trust what comes from env vars
        if (enabledIDPs) {
            const enabledList = enabledIDPs.split(',').map((id) => id.trim().toLowerCase())
            const isEnabled = enabledList.includes(idp)
            return isConfigured && isEnabled
        }

        // No env vars = no IDPs shown
        return false
    }

    useEffect(() => {
        idps.map((name) => {
            if (!isIdpValid(name)) {
                logger.error(
                    `IDP "${name}" is missing or has an invalid configuration in IDP_CONFIG. Valid IDPs are [${Object.keys(
                        IDP_CONFIG
                    ).join(', ')}].`
                )
            }
        })
    }, [idps])

    const onSocialLoginClick = async (name) => {
        try {
            setIsProcessingLogin(true)
            setSessionJSONItem('returnToPage', window.location.pathname)
            setSessionJSONItem('socialLoginProvider', name)
            setSessionJSONItem('brandId', 'GRG')

            const socialLoginManager = await getSocialLoginManager(name, 'GRG')
            setIsProcessingLogin(false)
            await socialLoginManager.signinRedirect()
        } catch (error) {
            const message = /redirect_uri doesn't match/.test(error.message)
                ? formatMessage(FEATURE_UNAVAILABLE_ERROR_MESSAGE)
                : formatMessage(API_ERROR_MESSAGE)
            form.setError('global', {type: 'manual', message})
            // GTM Modification
            sendViewErrorEvent(message)
            setIsProcessingLogin(false)
        }
    }

    return (
        idps &&
        enabledIDPs && (
            <>
                <Flex justify="center" align="center" gap={4}>
                    {idps
                        .filter((name) => isIdpValid(name))
                        .map((name) => {
                            const config = IDP_CONFIG[name.toLowerCase()]
                            const Icon = config?.icon
                            const message = formatMessage(config?.message)

                            return (
                                config && (
                                    <Button
                                        variant="ghost"
                                        key={name}
                                        onClick={() => {
                                            onSocialLoginClick(name)
                                        }}
                                        size="md"
                                        minW="revert"
                                        h="auto"
                                        p="1"
                                        borderRadius="full"
                                        aria-label={message}
                                    >
                                        <Icon size="20px" />
                                    </Button>
                                )
                            )
                        })}
                </Flex>
                {isProcessingLogin && (
                    <Box
                        background="whiteAlpha.600"
                        position="fixed"
                        top={0}
                        left={0}
                        w="100dvw"
                        h="100dvh"
                        zIndex="overlay"
                    >
                        <AbsoluteCenter>
                            <Spinner size="lg" />
                        </AbsoluteCenter>
                    </Box>
                )}
            </>
        )
    )
}

SocialLogin.propTypes = {
    form: PropTypes.object,
    idps: PropTypes.arrayOf(PropTypes.string),
    enabledIDPs: PropTypes.string,
    isCheckout: PropTypes.bool,
    callback: PropTypes.func
}

export default SocialLogin
